import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { diffLines } from 'diff';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/Layout';
import { PromptEditor } from '@/components/PromptEditor';
import { Badge, Button, Card, Dialog, Empty, ErrorNote, Field, Input, Loading, Textarea } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { fmtDateTime } from '@/lib/format';
import { canEdit, rk, useMe, useSnippets } from '@/lib/hooks';
import type { Snippet } from '@/lib/types';

export const Snippets = () => {
  const me = useMe();
  const q = useSnippets();
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const list = q.data?.snippets ?? [];
  const current = list.find((s) => s.id === selected) ?? list[0];

  return (
    <>
      <PageHeader
        title="Snippets"
        subtitle={
          <>
            Shared prompt blocks — safeguarding rules, tone, product facts. Include one in any prompt with <span className="mono">{'{{> slug}}'}</span>. Publishing a snippet updates every live agent that uses it.
          </>
        }
        actions={
          canEdit(me.data) && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" /> New snippet
            </Button>
          )
        }
      />
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorNote error={q.error} />
      ) : !list.length ? (
        <Card>
          <Empty title="No snippets yet">Pull repeated rules out of individual prompts so they're edited once.</Empty>
        </Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
          <Card>
            <ul>
              {list.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => setSelected(s.id)}
                    className={cn('flex w-full flex-col items-start gap-0.5 border-b border-line-2 px-4 py-2.5 text-left last:border-0 hover:bg-line-2/60', current?.id === s.id && 'bg-brand-soft')}
                  >
                    <span className="font-medium">{s.name}</span>
                    <span className="mono text-xs text-ink-3">{`{{> ${s.slug}}}`}</span>
                    <span className="flex gap-1">
                      {s.version ? <Badge tone="ok">v{s.version}</Badge> : <Badge>unpublished</Badge>}
                      {s.publishedContent !== s.content && <Badge tone="warn">unpublished edits</Badge>}
                      <Badge>{s.usedBy.length} agents</Badge>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          {current && <SnippetEditor key={current.id} snippet={current} editable={canEdit(me.data)} />}
        </div>
      )}
      <NewSnippet open={creating} onClose={() => setCreating(false)} onCreated={setSelected} />
    </>
  );
};

const SnippetEditor = ({ snippet, editable }: { snippet: Snippet; editable: boolean }) => {
  const qc = useQueryClient();
  const [content, setContent] = useState(snippet.content);
  const [name, setName] = useState(snippet.name);
  const [description, setDescription] = useState(snippet.description);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setContent(snippet.content), [snippet.content]);
  const dirty = content !== snippet.content || name !== snippet.name || description !== snippet.description;
  const pendingPublish = snippet.publishedContent !== snippet.content;
  const refresh = () => void qc.invalidateQueries({ queryKey: rk('snippets') });

  const save = useMutation({
    mutationFn: () => api(`/v1/snippets/${snippet.id}`, { method: 'PUT', body: { content, name, description } }),
    onSuccess: () => {
      toast.success('Saved — not live until published');
      refresh();
    },
  });
  const publish = useMutation({
    mutationFn: () => api<{ affected: Array<{ slug: string }> }>(`/v1/snippets/${snippet.id}/publish`, { body: {} }),
    onSuccess: (res) => {
      toast.success(res.affected.length ? `Published — updated ${res.affected.length} live agent(s)` : 'Published');
      setConfirming(false);
      refresh();
    },
  });
  const del = useMutation({
    mutationFn: () => api(`/v1/snippets/${snippet.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Deleted');
      refresh();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const parts = useMemo(() => diffLines(snippet.publishedContent ?? '', snippet.content), [snippet.publishedContent, snippet.content]);
  const live = snippet.usedBy.filter((u) => u.live);

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <Card
        title={<span className="mono">{`{{> ${snippet.slug}}}`}</span>}
        actions={
          editable && (
            <>
              <Button size="sm" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
              <Button size="sm" variant="primary" disabled={dirty || !pendingPublish} onClick={() => setConfirming(true)} title={dirty ? 'Save first' : undefined}>
                Publish…
              </Button>
            </>
          )
        }
      >
        <div className="flex flex-col gap-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name">
              <Input value={name} disabled={!editable} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Description">
              <Input value={description} disabled={!editable} onChange={(e) => setDescription(e.target.value)} />
            </Field>
          </div>
          <Field label="Content" hint="May use {{variables}} of the agents that include it">
            <PromptEditor value={content} onChange={setContent} readOnly={!editable} minHeight="260px" />
          </Field>
          <p className="text-xs text-ink-3">
            {snippet.publishedAt ? `v${snippet.version} published ${fmtDateTime(snippet.publishedAt)} by ${snippet.publishedBy}` : 'Never published — agents cannot include it yet.'}
          </p>
        </div>
      </Card>
      <Card title="Used by">
        {snippet.usedBy.length === 0 ? (
          <div className="flex items-center justify-between px-4 py-3 text-[13px] text-ink-3">
            Not used by any agent.
            {editable && (
              <Button size="sm" variant="danger" onClick={() => confirm('Delete this snippet?') && del.mutate()}>
                Delete
              </Button>
            )}
          </div>
        ) : (
          <ul>
            {snippet.usedBy.map((u) => (
              <li key={u.id} className="flex items-center gap-2 border-b border-line-2 px-4 py-2 last:border-0">
                <Link to="/agents/$agentId" params={{ agentId: u.id }} className="hover:underline">
                  {u.name}
                </Link>
                <span className="mono text-xs text-ink-3">{u.slug}</span>
                {u.live && <Badge tone="ok">live</Badge>}
                {u.draft && <Badge tone="warn">draft</Badge>}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        wide
        title={`Publish ${snippet.slug} v${snippet.version + 1}`}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Cancel</Button>
            <Button variant="primary" loading={publish.isPending} onClick={() => publish.mutate()}>
              Publish{live.length ? ` to ${live.length} live agent(s)` : ''}
            </Button>
          </>
        }
      >
        {live.length > 0 && (
          <p className="mb-3 text-[13px] text-warn">
            This changes the prompt of {live.map((u) => u.slug).join(', ')} in production within 30 seconds, without a new agent version.
          </p>
        )}
        <pre className="max-h-[50vh] overflow-auto rounded-md border border-line text-[12px] leading-relaxed">
          {parts.map((p, i) => (
            <span key={i} className={cn('block px-3 whitespace-pre-wrap', p.added && 'bg-ok-soft text-ok', p.removed && 'bg-bad-soft text-bad line-through', !p.added && !p.removed && 'text-ink-3')}>
              {p.value}
            </span>
          ))}
        </pre>
        {publish.error ? <ErrorNote error={publish.error} /> : null}
      </Dialog>
    </div>
  );
};

const NewSnippet = ({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ slug: '', name: '', description: '', content: '' });
  const create = useMutation({
    mutationFn: () => api<{ snippet: Snippet }>('/v1/snippets', { body: f }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: rk('snippets') });
      onCreated(res.snippet.id);
      setF({ slug: '', name: '', description: '', content: '' });
      onClose();
    },
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New snippet"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!f.slug || !f.name} loading={create.isPending} onClick={() => create.mutate()}>
            Create
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Name">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Safeguarding" autoFocus />
        </Field>
        <Field label="Slug" hint={`Included as {{> ${f.slug || 'slug'}}}`}>
          <Input className="mono" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, '-') })} placeholder="safeguarding" />
        </Field>
        <Field label="Content">
          <Textarea rows={6} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} />
        </Field>
        {create.error ? <ErrorNote error={create.error} /> : null}
      </div>
    </Dialog>
  );
};
